import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

interface FirmFormData {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    gstin: string;
    legal_name: string;
    industry: string;
    settings: any;
    logo_url?: string;
    banner_url?: string;
}

function FirmForm() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;

    const [formData, setFormData] = useState<FirmFormData>({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        gstin: '',
        legal_name: '',
        industry: '',
        settings: {}
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [error, setError] = useState<string | null>(null);
    const [industryFields, setIndustryFields] = useState<any[]>([]);
    const [industryFieldValues, setIndustryFieldValues] = useState<Record<string, any>>({});
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        if (isEditMode) {
            loadFirm();
        }
    }, [id]);

    const loadFirm = async () => {
        try {
            const response = await api.get(`/firms/${id}`);
            const firmData = response.firm || response.data?.firm || response.data;
            if (firmData) {
                setFormData(prev => ({
                    ...prev,
                    ...firmData
                }));

                // Load industry fields if industry is set
                if (firmData.industry) {
                    await loadIndustryFields(firmData.industry);

                    // Set existing industry field values
                    if (firmData.settings?.industry_fields) {
                        setIndustryFieldValues(firmData.settings.industry_fields);
                    }
                }
                if (firmData.logo_url) {
                    setLogoPreview(firmData.logo_url);
                }
            }
        } catch (err: any) {
            console.error('Failed to load firm:', err);
            setError('Failed to load firm details');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // If industry changed, load industry-specific fields
        if (name === 'industry' && value) {
            loadIndustryFields(value);
        }
    };

    const loadIndustryFields = async (industry: string) => {
        try {
            const response = await api.get(`/product_field_definitions?industry=${industry}`);
            const fields = response.data || response.product_field_definitions || [];
            setIndustryFields(fields);

            // Initialize field values from settings if editing
            if (formData.settings?.industry_fields) {
                setIndustryFieldValues(formData.settings.industry_fields);
            }
        } catch (err) {
            console.error('Failed to load industry fields:', err);
        }
    };

    const handleIndustryFieldChange = (fieldId: string, value: any) => {
        setIndustryFieldValues(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadLogo = async (firmId: string) => {
        if (!logoFile) return;
        setUploadingLogo(true);
        try {
            const formData = new FormData();
            formData.append('logo', logoFile);
            await api.post(`/firms/${firmId}/upload_logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } catch (err) {
            console.error('Failed to upload logo:', err);
            throw new Error('Logo upload failed');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = { firm: formData };
            let response;

            // Add industry field values to settings
            if (Object.keys(industryFieldValues).length > 0) {
                payload.firm.settings = {
                    ...payload.firm.settings,
                    industry_fields: industryFieldValues
                };
            }

            console.log('Submitting firm data:', payload);

            if (isEditMode) {
                response = await api.put(`/firms/${id}`, payload);
            } else {
                response = await api.post('/firms', payload);
            }

            const firmId = response.id || response.data?.id || response.firm?.id;
            if (firmId && logoFile) {
                await uploadLogo(id || firmId);
            }

            console.log('Firm save response:', response);

            if (response.success || response.id || response.data?.id || response.firm) {
                // Show success message
                const successMessage = isEditMode ? 'Business profile updated successfully!' : 'Business profile created successfully!';

                // Create a temporary success notification
                const notification = document.createElement('div');
                notification.className = 'fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg font-medium';
                notification.textContent = successMessage;
                document.body.appendChild(notification);

                setTimeout(() => {
                    notification.remove();
                }, 3000);

                // Navigate after short delay
                setTimeout(() => {
                    if (!isEditMode) {
                        window.location.href = '/';
                    } else {
                        navigate('/firms');
                    }
                }, 1000);
            } else {
                setError(response.errors ? response.errors.join(', ') : 'Operation failed. Please try again.');
            }
        } catch (err: any) {
            console.error('Firm save error:', err);
            setError(err.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return <div className="min-h-screen flex items-center justify-center font-medium text-slate-500">Loading firm details...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                    {isEditMode ? 'Edit Business profile' : 'Setup your Business'}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                    {isEditMode ? 'Update your organization details and industry settings' : 'Get started by creating your primary business profile'}
                </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
                <div className="p-6">
                    <form className="space-y-8" onSubmit={handleSubmit}>
                        {error && (
                            <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-xl flex items-center gap-3">
                                <svg className="w-5 h-5 text-rose-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-rose-800 font-medium">{error}</p>
                            </div>
                        )}

                        <div className="space-y-6">
                            <h3 className="text-sm font-semibold text-slate-900 border-b border-slate-100 pb-2">Business Identity</h3>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div className="sm:col-span-2">
                                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                                        Business Profile Picture / Logo
                                    </label>
                                    <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
                                        <div className="relative w-24 h-24 bg-white rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center">
                                            {logoPreview || formData.logo_url ? (
                                                <img src={logoPreview || formData.logo_url} className="w-full h-full object-contain" alt="Logo" />
                                            ) : (
                                                <div className="text-3xl text-slate-300">🏢</div>
                                            )}
                                            {uploadingLogo && (
                                                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                                    <svg className="animate-spin h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            <div>
                                                <p className="text-slate-500 text-sm">Upload your business logo for invoices and public storefronts.</p>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-3">
                                                <input
                                                    type="file"
                                                    id="logo-upload"
                                                    accept="image/*"
                                                    className="hidden"
                                                    onChange={handleLogoChange}
                                                />
                                                <label
                                                    htmlFor="logo-upload"
                                                    className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-md text-sm font-medium hover:bg-indigo-100 cursor-pointer transition-colors"
                                                >
                                                    Select New Logo
                                                </label>
                                                {(formData.logo_url || logoPreview) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setLogoFile(null);
                                                            setLogoPreview(null);
                                                            setFormData(p => ({ ...p, logo_url: '' }));
                                                        }}
                                                        className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-400">Supports PNG, JPG, SVG (Max 5MB)</p>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                                        Organization Name <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="e.g. Global Tech Solutions"
                                        className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all font-medium text-slate-800"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="industry" className="block text-sm font-medium text-slate-700 mb-1">
                                        Primary Industry <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        id="industry"
                                        name="industry"
                                        required
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none bg-white text-sm font-medium text-slate-700"
                                        value={formData.industry || ''}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Industry</option>
                                        <option value="electronics">Electronics</option>
                                        <option value="vehicle">Vehicle / Automobile</option>
                                        <option value="grocery">Grocery / Supermarket</option>
                                        <option value="fashion">Fashion / Apparel</option>
                                        <option value="pharmacy">Pharmacy / Healthcare</option>
                                        <option value="retail">General Retail</option>
                                        <option value="rental">Rental Services</option>
                                        <option value="dairy">Milk & Dairy</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="legal_name" className="block text-sm font-medium text-slate-700 mb-1">
                                        Legal Entity Name
                                    </label>
                                    <input
                                        id="legal_name"
                                        name="legal_name"
                                        type="text"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                        value={formData.legal_name || ''}
                                        onChange={handleChange}
                                        placeholder="Registered name for invoices"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <h3 className="text-sm font-semibold text-slate-900 border-b border-slate-100 pb-2">Contact Details</h3>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                                        Business Email
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                        value={formData.email || ''}
                                        onChange={handleChange}
                                        placeholder="contact@company.com"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="text"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                        value={formData.phone || ''}
                                        onChange={handleChange}
                                        placeholder="+91 00000 00000"
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="gstin" className="block text-sm font-medium text-slate-700 mb-1">
                                        GSTIN / Tax ID
                                    </label>
                                    <input
                                        id="gstin"
                                        name="gstin"
                                        type="text"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm uppercase"
                                        value={formData.gstin || ''}
                                        onChange={handleChange}
                                        placeholder="Enter GST number"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <h3 className="text-sm font-semibold text-slate-900 border-b border-slate-100 pb-2">Location</h3>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <label htmlFor="address" className="block text-sm font-medium text-slate-700 mb-1">
                                        Full Address
                                    </label>
                                    <textarea
                                        id="address"
                                        name="address"
                                        rows={3}
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none resize-none text-sm"
                                        value={formData.address || ''}
                                        onChange={handleChange}
                                        placeholder="Street address, suite, etc."
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="city" className="block text-sm font-medium text-slate-700 mb-1">
                                        City
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        id="city"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                        value={formData.city || ''}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="state" className="block text-sm font-medium text-slate-700 mb-1">
                                        State
                                    </label>
                                    <input
                                        type="text"
                                        name="state"
                                        id="state"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                        value={formData.state || ''}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="sm:col-span-2">
                                    <label htmlFor="pincode" className="block text-sm font-medium text-slate-700 mb-1">
                                        Pincode
                                    </label>
                                    <input
                                        type="text"
                                        name="pincode"
                                        id="pincode"
                                        className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                        value={formData.pincode || ''}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Industry-Specific Attributes */}
                        {industryFields.length > 0 && (
                            <div className="space-y-6">
                                <h3 className="text-sm font-semibold text-slate-900 border-b border-slate-100 pb-2">
                                    Tailored Industry Attributes
                                </h3>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    {industryFields.map((field: any) => (
                                        <div key={field.id || field.field_key} className={field.field_type === 'textarea' ? 'sm:col-span-2' : ''}>
                                            <label htmlFor={`industry_field_${field.id || field.field_key}`} className="block text-sm font-medium text-slate-700 mb-1">
                                                {field.field_label || field.label}
                                                {field.required && <span className="text-rose-500">*</span>}
                                            </label>
                                            {(field.field_type === 'text' || field.field_type === 'string') && (
                                                <input
                                                    id={`industry_field_${field.id || field.field_key}`}
                                                    type="text"
                                                    required={field.required}
                                                    className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                                    value={industryFieldValues[field.id || field.field_key] || ''}
                                                    onChange={(e) => handleIndustryFieldChange(field.id || field.field_key, e.target.value)}
                                                    placeholder={field.placeholder || ''}
                                                />
                                            )}
                                            {(field.field_type === 'text_area' || field.field_type === 'textarea') && (
                                                <textarea
                                                    id={`industry_field_${field.id || field.field_key}`}
                                                    required={field.required}
                                                    rows={3}
                                                    className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none resize-none text-sm"
                                                    value={industryFieldValues[field.id || field.field_key] || ''}
                                                    onChange={(e) => handleIndustryFieldChange(field.id || field.field_key, e.target.value)}
                                                    placeholder={field.placeholder || ''}
                                                />
                                            )}
                                            {(field.field_type === 'number' || field.field_type === 'integer') && (
                                                <input
                                                    id={`industry_field_${field.id || field.field_key}`}
                                                    type="number"
                                                    required={field.required}
                                                    className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none text-sm"
                                                    value={industryFieldValues[field.id || field.field_key] || ''}
                                                    onChange={(e) => handleIndustryFieldChange(field.id || field.field_key, e.target.value)}
                                                    placeholder={field.placeholder || ''}
                                                />
                                            )}
                                            {(field.field_type === 'select' || field.field_type === 'dropdown') && field.choices && (
                                                <select
                                                    id={`industry_field_${field.id || field.field_key}`}
                                                    required={field.required}
                                                    className="w-full px-3 py-2 rounded-md border border-slate-200 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none bg-white text-sm text-slate-700"
                                                    value={industryFieldValues[field.id || field.field_key] || ''}
                                                    onChange={(e) => handleIndustryFieldChange(field.id || field.field_key, e.target.value)}
                                                >
                                                    <option value="">Select {field.field_label || field.label}</option>
                                                    {field.choices.map((opt: string) => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 pt-6 border-t border-slate-100">
                            {isEditMode && (
                                <button
                                    type="button"
                                    onClick={() => navigate('/firms')}
                                    className="px-4 py-2 bg-white border border-slate-200 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className={`px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    isEditMode ? 'Update Business profile' : 'Complete Setup'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default FirmForm;
